// src/api/axios.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const axiosInstance = axios.create({
    baseURL: API_URL,
    withCredentials: true, // This is crucial for sending cookies
});

// This function will be used by our AuthContext to set the token
export const setAuthToken = (token) => {
    if (token) {
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        delete axiosInstance.defaults.headers.common['Authorization'];
    }
};

// --- NEW: Refresh Queue System ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// --- NEW: Interceptor ID tracker to prevent duplicates ---
let interceptorId = null;

// Interceptor to handle token refresh and Session Expiry
export const setupAxiosInterceptor = (logoutUser, getNewAccessToken) => {
    
    // 1. Eject any existing interceptor to prevent the "Zombie State" duplicate bug
    if (interceptorId !== null) {
        axiosInstance.interceptors.response.eject(interceptorId);
    }

    interceptorId = axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            const currentPath = window.location.pathname;
            
            // --- FIX 1: THE WHITELIST ---
            // Added /session-expired so we don't infinitely reload it!
            const isPublicPage = 
                currentPath === '/' || 
                currentPath === '/login' ||
                currentPath === '/session-expired' || 
                currentPath === '/2fa-setup' ||
                currentPath === '/otp-verification' ||
                currentPath === '/forgot-password' ||
                currentPath === '/change-password' ||
                currentPath.startsWith('/reset-password/');

            // --- FIX 2: THE REFRESH TRAP ---
            if (originalRequest.url.includes('/token/refresh') || originalRequest.url.includes('/refresh')) {
                // ONLY redirect if we are inside the app. If we are already on the login 
                // or expired page, just quietly fail and let the user log in.
                if (!isPublicPage) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/session-expired';
                }
                return Promise.reject(error);
            }

            // --- FIX 3: IGNORE LOGOUT REQUESTS ---
            const isAuthRequest = 
                originalRequest.url.includes('/token') || 
                originalRequest.url.includes('/login') ||
                originalRequest.url.includes('/logout') || // <--- Added this!
                originalRequest.url.includes('/otp') ||
                originalRequest.url.includes('/password');

            // 3. LOGIC: Only trigger refresh if...
            if (
                error.response?.status === 401 && 
                !originalRequest._retry && 
                !isAuthRequest &&
                !isPublicPage
            ) {
                // If a refresh is already happening, pause this request and add it to the queue
                if (isRefreshing) {
                    return new Promise(function(resolve, reject) {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        originalRequest.headers['Authorization'] = 'Bearer ' + token;
                        return axiosInstance(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    // Attempt to refresh the token transparently
                    const newAccessToken = await getNewAccessToken();
                    setAuthToken(newAccessToken);
                    
                    // Release the queue so paused requests can finish
                    processQueue(null, newAccessToken);
                    
                    originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                    return axiosInstance(originalRequest);
                } catch (refreshError) {
                    // If refresh fails, reject the whole queue and force redirect
                    processQueue(refreshError, null);
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/session-expired';
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }
            
            return Promise.reject(error);
        }
    );
};

export default axiosInstance;